/**
 * React Query hook for fetching environment-scoped deployment status per artifact.
 * Returns the most recent deployment outcome (success/failure) for each artifact
 * within the selected environment.
 */

import { useQuery } from '@tanstack/react-query';

import type { DeploymentSummary } from '@/api/deploy';
import { listDeployments } from '@/api/deploy';
import { queryKeys } from '@/modules/query/query-keys';

// --- Types ---

export interface ArtifactDeployStatusData {
  lastSuccess?: DeploymentSummary;
  lastFailure?: DeploymentSummary;
}

export type ArtifactDeployStatusMap = Record<string, ArtifactDeployStatusData>;

// --- Hook ---

/**
 * Fetches environment-scoped deployment statuses for all artifacts in a project.
 * Returns a map of artifactId -> { lastSuccess, lastFailure } for the given environment.
 *
 * Refreshes within 3 seconds of environment change by keying on environmentId.
 * Uses a short staleTime so data is refetched promptly on environment switch.
 */
export function useArtifactDeployStatuses(projectKey: string, environmentId: string | undefined) {
  return useQuery<ArtifactDeployStatusMap>({
    queryKey: queryKeys.deployments.artifactStatuses(projectKey, environmentId ?? ''),
    queryFn: async () => {
      if (!environmentId) return {};

      // Fetch recent deployments for this environment (enough to capture last success/failure per artifact)
      const result = await listDeployments({
        projectKey,
        environment: environmentId,
        size: 100,
      });

      const map: ArtifactDeployStatusMap = {};
      for (const dep of result.content) {
        const key = dep.artifactId;
        if (!key) continue;
        const entry = map[key] ?? (map[key] = {});
        if (dep.status === 'SUCCESS' && !entry.lastSuccess) {
          entry.lastSuccess = dep;
        } else if (dep.status === 'FAILED' && !entry.lastFailure) {
          entry.lastFailure = dep;
        }
      }
      return map;
    },
    enabled: !!projectKey && !!environmentId,
    retry: 1,
    staleTime: 0, // Always refetch on environment change for responsiveness
  });
}
